/**
 * Machine Learning Insights Component
 * Displays detailed ML optimization results and guest behavior analysis
 */

import React, { useState, useEffect } from "react";
import "./MLInsights.css";
import { machineLearningOptimizationService } from "../services/machineLearningOptimizationService";

const MLInsights = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState("behavior");
  const [timeRange, setTimeRange] = useState("30d");

  useEffect(() => {
    const loadInsights = async () => {
      try {
        setLoading(true);

        const [behaviorInsights, seasonalData, groupData, regionalData] =
          await Promise.all([
            machineLearningOptimizationService.getGuestBehaviorInsights(),
            machineLearningOptimizationService.getSeasonalOptimization(),
            machineLearningOptimizationService.getGroupDiningInsights(),
            machineLearningOptimizationService.getRegionalPreferences(),
          ]);

        setInsights({
          behavior: behaviorInsights,
          seasonal: seasonalData,
          group: groupData,
          regional: regionalData,
        });
      } catch (error) {
        console.error("Error loading ML insights:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInsights();
  }, [timeRange]);

  const renderBehaviorInsights = () => {
    if (!insights?.behavior) return null;

    return (
      <div className="insights-section">
        <h3>🧠 Guest Behavior Patterns</h3>

        <div className="behavior-grid">
          <div className="behavior-card">
            <h4>Preference Clusters</h4>
            <div className="cluster-list">
              {insights.behavior.clusters?.map((cluster) => (
                <div key={cluster.id} className="cluster-item">
                  <div className="cluster-header">
                    <span className="cluster-name">{cluster.name}</span>
                    <span className="cluster-size">{cluster.size} guests</span>
                  </div>
                  <div className="cluster-preferences">
                    {cluster.preferences?.slice(0, 3).map((pref) => (
                      <span key={pref} className="preference-tag">
                        {pref}
                      </span>
                    ))}
                  </div>
                </div>
              )) || <p>No clusters identified</p>}
            </div>
          </div>

          <div className="behavior-card">
            <h4>Decision Patterns</h4>
            <div className="pattern-metrics">
              <div className="metric">
                <span>Avg. Decision Time:</span>
                <span>{insights.behavior.avgDecisionTime || "2.3m"}</span>
              </div>
              <div className="metric">
                <span>Price Sensitivity:</span>
                <span className="sensitivity-level">
                  {insights.behavior.priceSensitivity || "Medium"}
                </span>
              </div>
              <div className="metric">
                <span>Menu Exploration:</span>
                <span>{insights.behavior.menuExploration || "68%"}</span>
              </div>
            </div>
          </div>

          <div className="behavior-card">
            <h4>Recommendation Success</h4>
            <div className="success-chart">
              <div className="success-rate">
                <span className="rate-value">
                  {insights.behavior.recommendationSuccess || "87%"}
                </span>
                <span className="rate-label">Success Rate</span>
              </div>
              <div className="success-breakdown">
                <div className="breakdown-item">
                  <span>Wine Recommendations:</span>
                  <span>{insights.behavior.wineSuccess || "91%"}</span>
                </div>
                <div className="breakdown-item">
                  <span>Spirit Recommendations:</span>
                  <span>{insights.behavior.spiritSuccess || "84%"}</span>
                </div>
                <div className="breakdown-item">
                  <span>Beer Recommendations:</span>
                  <span>{insights.behavior.beerSuccess || "76%"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSeasonalInsights = () => {
    if (!insights?.seasonal) return null;

    return (
      <div className="insights-section">
        <h3>🌱 Seasonal Optimization</h3>

        <div className="seasonal-grid">
          {Object.entries(insights.seasonal.seasons || {}).map(
            ([season, data]) => (
              <div key={season} className="seasonal-card">
                <h4>{season.charAt(0).toUpperCase() + season.slice(1)}</h4>
                <div className="seasonal-trends">
                  <div className="trend-item">
                    <span>Popular Category:</span>
                    <span className="category-name">
                      {data.topCategory || "Red Wines"}
                    </span>
                  </div>
                  <div className="trend-item">
                    <span>Demand Change:</span>
                    <span className={`trend-value ${data.trend || "up"}`}>
                      {data.demandChange || "+15%"}
                    </span>
                  </div>
                  <div className="trend-item">
                    <span>Price Tolerance:</span>
                    <span>{data.priceTolerance || "High"}</span>
                  </div>
                </div>
                <div className="seasonal-recommendations">
                  <h5>Top Recommendations:</h5>
                  <ul>
                    {data.recommendations
                      ?.slice(0, 3)
                      .map((rec) => <li key={rec}>{rec}</li>) || (
                      <li>No recommendations available</li>
                    )}
                  </ul>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  const renderGroupInsights = () => {
    if (!insights?.group) return null;

    return (
      <div className="insights-section">
        <h3>👥 Group Dining Patterns</h3>

        <div className="group-analysis">
          <div className="group-metrics">
            <div className="metric-card">
              <h4>Average Group Size</h4>
              <div className="metric-value">
                {insights.group.avgSize || "3.2"}
              </div>
              <div className="metric-change">+0.3 from last month</div>
            </div>

            <div className="metric-card">
              <h4>Group Consensus Rate</h4>
              <div className="metric-value">
                {insights.group.consensusRate || "73%"}
              </div>
              <div className="metric-change">+5% improvement</div>
            </div>

            <div className="metric-card">
              <h4>Split Bill Frequency</h4>
              <div className="metric-value">
                {insights.group.splitBillRate || "45%"}
              </div>
              <div className="metric-change">Standard trend</div>
            </div>
          </div>

          <div className="group-preferences">
            <h4>Group Size Preferences</h4>
            <div className="size-preferences">
              {insights.group.sizePreferences?.map((sizePref) => (
                <div key={sizePref.size} className="size-pref-item">
                  <span className="size-label">{sizePref.size} people</span>
                  <div className="pref-details">
                    <span>Preferred: {sizePref.preferred}</span>
                    <span>Avg Spend: ${sizePref.avgSpend}</span>
                  </div>
                </div>
              )) || <p>No size preference data available</p>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRegionalInsights = () => {
    if (!insights?.regional) return null;

    return (
      <div className="insights-section">
        <h3>🌍 Regional Preferences</h3>

        <div className="regional-grid">
          {insights.regional.regions?.map((region) => (
            <div key={region.name} className="regional-card">
              <h4>{region.name}</h4>
              <div className="regional-stats">
                <div className="stat-item">
                  <span>Market Share:</span>
                  <span>{region.marketShare || "0%"}</span>
                </div>
                <div className="stat-item">
                  <span>Growth Rate:</span>
                  <span
                    className={`growth-rate ${region.growthTrend || "stable"}`}
                  >
                    {region.growthRate || "0%"}
                  </span>
                </div>
              </div>
              <div className="regional-preferences">
                <h5>Top Preferences:</h5>
                <div className="preference-tags">
                  {region.preferences?.slice(0, 4).map((pref) => (
                    <span key={pref} className="pref-tag">
                      {pref}
                    </span>
                  )) || <span>No data</span>}
                </div>
              </div>
            </div>
          )) || <p>No regional data available</p>}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="ml-insights loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading ML insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ml-insights">
      <header className="insights-header">
        <h2>🤖 Machine Learning Insights</h2>
        <div className="header-controls">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-range-select"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 3 months</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </header>

      <nav className="insights-nav">
        <button
          className={selectedView === "behavior" ? "active" : ""}
          onClick={() => setSelectedView("behavior")}
        >
          Guest Behavior
        </button>
        <button
          className={selectedView === "seasonal" ? "active" : ""}
          onClick={() => setSelectedView("seasonal")}
        >
          Seasonal Trends
        </button>
        <button
          className={selectedView === "group" ? "active" : ""}
          onClick={() => setSelectedView("group")}
        >
          Group Dynamics
        </button>
        <button
          className={selectedView === "regional" ? "active" : ""}
          onClick={() => setSelectedView("regional")}
        >
          Regional Analysis
        </button>
      </nav>

      <main className="insights-content">
        {selectedView === "behavior" && renderBehaviorInsights()}
        {selectedView === "seasonal" && renderSeasonalInsights()}
        {selectedView === "group" && renderGroupInsights()}
        {selectedView === "regional" && renderRegionalInsights()}
      </main>
    </div>
  );
};

export default MLInsights;
